import React, { useState, useEffect, useRef } from 'react';
import './OneToOneChat.css';
import { IParticipant } from '../../models/IParticipant';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { isNewParticipantJoinedState, oneToOneChatParticipantIdState } from '../../atom/atom';
import { useRoomContext } from '@livekit/components-react';
import { RemoteParticipant } from 'livekit-client';

const OneToOneChat: React.FC = () => {
  const room = useRoomContext();

  const [participants, setParticipants] = useState<IParticipant[]>([]);
  const [searchTerm, setSearchTerm] = useState('Everyone');
  const setParticipantId = useSetRecoilState(oneToOneChatParticipantIdState);

  const [isNewPartidcipantAdded, setIsNewPartidcipantAdded] = useRecoilState(isNewParticipantJoinedState);

  const [filteredParticipants, setFilteredParticipants] = useState<IParticipant[]>([
    { participantName: 'Everyone', participantId: '0' },
  ]);

  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 🔹 Update participant list when someone joins/leaves
  useEffect(() => {
    const allParticipants = room.participants;
    transformParticipants(allParticipants);

    setIsNewPartidcipantAdded(false);
  }, [isNewPartidcipantAdded, room.participants]);

  async function transformParticipants(participantsMap: Map<string, RemoteParticipant>) {
    const participantsArray: IParticipant[] = [];
    participantsMap.forEach((value, key) => {
      participantsArray.push({
        participantName: value.name || '',
        participantId: key,
      });
    });
    setParticipants(participantsArray);
  }

  // 🔹 Always recompute filtered list when participants OR searchTerm changes
  useEffect(() => {
    setFilteredParticipants([
      { participantName: 'Everyone', participantId: '0' },
      ...participants.filter((participant) =>
        participant.participantName.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    ]);
  }, [participants, searchTerm]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setIsDropdownVisible(true);
  };

  const handleParticipantSelect = (participant: IParticipant) => {
    if (participant.participantId !== '0') {
      setParticipantId({ participantId: participant.participantId, isParticipantSelected: true });
    } else {
      setParticipantId({ participantId: '0', isParticipantSelected: false });
    }

    setSearchTerm(participant.participantName);
    setIsDropdownVisible(false);
  };

  const handleInputClick = () => {
    setIsDropdownVisible(true);
  };

  const handleOutsideClick = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsDropdownVisible(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  return (
    <div className="dropdown-container" ref={dropdownRef}>
      <input
        type="text"
        placeholder="Participant Name"
        value={searchTerm}
        onChange={handleSearchChange}
        onClick={handleInputClick}
        className="search-input"
      />
      {isDropdownVisible && (
        <div className="dropdown-list">
          {filteredParticipants.map((participant) => (
            <div
              key={participant.participantId}
              className="dropdown-item"
              onClick={() => handleParticipantSelect(participant)}
            >
              {participant.participantName}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OneToOneChat;
